import * as React from 'react';
import {FunctionComponent} from 'react';
import {classNames} from '../../Support/classNames';
import {format} from 'date-fns';
import {formatDistance} from '../../Support/Formatting/formatDistance';
import {formatSplit} from '../../Support/Formatting/formatSplit';
import {formatStrokeRate} from '../../Support/Formatting/formatStrokeRate';
import {Distance} from '../../../Domain/Distance';
import {Speed} from '../../../Domain/Speed';
import {StrokeRate} from '../../../Domain/StrokeRate';
import {PartialWorkout} from '../../../Models/PartialWorkout';
import {Duration} from '../../../Domain/Duration';
import {formatDuration} from '../../Support/Formatting/formatDuration';
import {workoutTitleFormatter} from '../../Support/workoutTitleFormatter';
import {Link} from 'react-router-dom';
import { calculateIndoorWorkoutListSummary } from '../../../Strokes/calculateIndoorWorkoutListSummary';

interface OwnProps {
    workoutListViewModels: WorkoutListViewModel[];
    selectable?: boolean;
    selectedWorkoutIds?: number[];
    onSelectionChanged?: (workoutIds: number[]) => void;
    userList?: Record<number, string>;
}

export const WorkoutList: FunctionComponent<OwnProps> = ({
    workoutListViewModels,
    selectable,
    selectedWorkoutIds,
    onSelectionChanged,
    userList,
}) => {
    return (
        <div className="workoutlist">
            <table
                className={classNames('table workoutlist__table', {
                    'workoutlist__table--empty': workoutListViewModels.length === 0,
                })}
            >
                <thead>
                    <tr>
                        <th className="workoutlist__table_cell workoutlist__table_cell--wide">Title</th>
                        {userList && <th className="workoutlist__table_cell workoutlist__table_cell--wide">Athlete</th>}
                        <th className="workoutlist__table_cell">Date</th>
                        <th className="workoutlist__table_cell workoutlist__table_cell--rightalined">Duration</th>
                        <th className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                            Distance <span className="workoutlist__table_cell-label">(m)</span>
                        </th>
                        <th className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                            Ø Split <span className="workoutlist__table_cell-label">(/500m)</span>
                        </th>
                        {!userList && (
                            <th className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                                Ø Stroke rate <span className="workoutlist__table_cell-label">(/min)</span>
                            </th>
                        )}
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {workoutListViewModels.length === 0 && (
                        <tr>
                            <td colSpan={7}>
                                <span className="workoutlist__empty">
                                    <span className="workoutlist__empty_image" />
                                    <span className="workoutlist__empty_text">No workouts found</span>
                                </span>
                            </td>
                        </tr>
                    )}
                    {workoutListViewModels.map((workout) => {
                        return (
                            <tr key={workout.id} className="workoutlist__row">
                                <td className="workoutlist__table_cell workoutlist__table_cell--wide">
                                    {selectable && (
                                        <input
                                            type="checkbox"
                                            className={classNames(
                                                'workoutlist__checkbox workoutlist__checkbox--appearing',
                                                {
                                                    'workoutlist__checkbox--visible':
                                                        selectedWorkoutIds !== undefined &&
                                                        selectedWorkoutIds?.length > 0,
                                                },
                                            )}
                                            checked={selectedWorkoutIds?.includes(workout.id)}
                                            onChange={(e) => {
                                                if (onSelectionChanged) {
                                                    if ((e.nativeEvent as any).shiftKey) {
                                                        const lastSelectedWorkoutId =
                                                            selectedWorkoutIds?.[selectedWorkoutIds?.length - 1];
                                                        const lastSelectedWorkoutIndex =
                                                            workoutListViewModels.findIndex(
                                                                (w) => w.id === lastSelectedWorkoutId,
                                                            );
                                                        const currentWorkoutIndex = workoutListViewModels.findIndex(
                                                            (w) => w.id === workout.id,
                                                        );
                                                        const workoutIdsToSelect = workoutListViewModels
                                                            .slice(
                                                                Math.min(lastSelectedWorkoutIndex, currentWorkoutIndex),
                                                                Math.max(
                                                                    lastSelectedWorkoutIndex,
                                                                    currentWorkoutIndex,
                                                                ) + 1,
                                                            )
                                                            .map((w) => w.id);
                                                        onSelectionChanged(workoutIdsToSelect);
                                                    } else {
                                                        onSelectionChanged(
                                                            selectedWorkoutIds?.includes(workout.id)
                                                                ? selectedWorkoutIds.filter((id) => id !== workout.id)
                                                                : [...(selectedWorkoutIds || []), workout.id],
                                                        );
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    <Link to={'/workouts/show/' + workout.id}>{workout.title}</Link>
                                </td>
                                {userList && <td className="workoutlist__table_cell">{userList[workout.userId]}</td>}
                                <td className="workoutlist__table_cell">{format(workout.executedAt, 'dd-MM-yyyy')}</td>
                                <td className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                                    {formatDuration(workout.duration).formatted}
                                </td>
                                <td className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                                    {formatDistance(workout.distance).formatted}
                                </td>
                                <td className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                                    {formatSplit(workout.speed).formatted}
                                </td>
                                {!userList && (
                                    <td className="workoutlist__table_cell workoutlist__table_cell--rightalined">
                                        {formatStrokeRate(workout.strokeRate).formatted}
                                    </td>
                                )}
                                <td></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export class WorkoutListViewModel {
    public constructor(
        public id: number,
        public userId: number,
        public title: string,
        public executedAt: Date,
        public duration: Duration,
        public distance: Distance,
        public speed: Speed,
        public strokeRate: StrokeRate | null,
    ) {}

    public static fromPartialWorkout(partialWorkout: PartialWorkout): WorkoutListViewModel {

        const indoorWorkoutListSummary = calculateIndoorWorkoutListSummary(partialWorkout);
        
        return new WorkoutListViewModel(
            partialWorkout.id,
            partialWorkout.user_id,
            workoutTitleFormatter(partialWorkout),
            partialWorkout.executed_at,
            indoorWorkoutListSummary.duration,
            indoorWorkoutListSummary.distance,
            Speed.fromDistanceAndDuration(indoorWorkoutListSummary.distance, indoorWorkoutListSummary.duration),
            StrokeRate.fromStrokeCountAndDuration(indoorWorkoutListSummary.strokeCount, indoorWorkoutListSummary.duration),
        );
    }
}
